/* eslint-disable no-param-reassign */
import { buildMonoShades } from "@editor/modules/settings/components/controls/ChartColors/chart-color-palettes";
import { barData } from "./bar";
import { BarLineData } from "./bar-line";
import { getVariableColor } from "./get-css-variable";
import { lineData } from "./line";
import { pieData } from "./pie";
import { raceData } from "./race";
import { scatterData } from "./scatter";
import { stackedData } from "./stacket";

const handler: Record<string, unknown> = {
  horizontal: barData,
  vertical: barData,
  pie: pieData,
  donut: pieData,
  line: lineData,
  "vertical-line": lineData,
  "vertical-smooth-line": lineData,
  "horizontal-smooth-line": lineData,
  "vertical-area": lineData,
  "horizontal-area": lineData,
  "vertical-smooth-area": lineData,
  "horizontal-smooth-area": lineData,
  "vertical-stacked": stackedData,
  "horizontal-stacked": stackedData,
  "horizontal-scatter": scatterData,
  "bar-line": BarLineData,
  "horizontal-race": raceData,
  "vertical-race": raceData,
};

export const parser = (
  type: string,
  payload: Record<string, unknown>,
): {
  rowsLength: number;
  columnsLength: number;
  forceColumns?: boolean;
  option: Record<string, unknown>;
} => {
  if (
    !payload.data ||
    (Array.isArray(payload.data) && payload.data.length === 0)
  )
    return { rowsLength: 0, columnsLength: 0, option: {} };
  //  ============ helpers =============
  const rawColors = (payload.colors as string[]) ?? [];
  const monoBase = rawColors[0];
  // A mono palette whose base is a design-system variable is rebuilt from the
  // resolved base, so the shade gradient tracks the current theme instead of
  // the colors baked in when the slide was created. If the variable can't be
  // resolved (empty), fall back to resolving each stored color individually
  // rather than feeding an unparseable "var(...)" string into buildMonoShades.
  const resolvedMonoBase =
    payload.colorPaletteType === "mono" &&
    typeof monoBase === "string" &&
    monoBase.includes("var(")
      ? getVariableColor(monoBase)
      : undefined;
  const colors = resolvedMonoBase
    ? buildMonoShades(resolvedMonoBase)
    : rawColors.map((color: string) => getVariableColor(color));
  payload.color = getVariableColor(payload.color as string | undefined);
  const primaryColor = getVariableColor("var(--theme-color-1)");
  if (payload.darkMode) payload.color = "#FFFFFF";
  const data = (payload.data as string[][]).map((row: (string | number)[]) => [
    ...row,
  ]);
  //  ============ helpers =============

  const func = handler[type];
  if (!func || typeof func !== "function") return { rowsLength: 0, columnsLength: 0, option: {} };
  const result = func({ data, colors, primaryColor, type, payload });
  if (payload.darkMode && result?.option) {
    result.option.darkMode = true;
  }
  if (result?.option) {
    applyValueAxisBounds(result.option, payload);
  }
  return result;
};

const parseBound = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
};

const getValueFromDatum = (datum: unknown): number => {
  if (typeof datum === "number") return datum;
  if (datum && typeof datum === "object" && "value" in datum) {
    const v = (datum as { value: unknown }).value;
    if (typeof v === "number") return v;
    if (typeof v === "string" && Number.isFinite(Number(v))) return Number(v);
  }
  if (typeof datum === "string" && Number.isFinite(Number(datum)))
    return Number(datum);
  return Number.NaN;
};

const getDataMax = (
  series: Array<{ data?: unknown[]; stack?: string }>,
): number | undefined => {
  if (series.length === 0) return undefined;
  const isStacked = series.some((s) => !!s.stack);
  let maxValue = Number.NEGATIVE_INFINITY;
  if (isStacked) {
    const length = series.reduce(
      (acc, s) => Math.max(acc, s.data?.length ?? 0),
      0,
    );
    for (let i = 0; i < length; i++) {
      let sum = 0;
      for (const s of series) {
        const n = getValueFromDatum(s.data?.[i]);
        if (Number.isFinite(n)) sum += n;
      }
      if (sum > maxValue) maxValue = sum;
    }
  } else {
    for (const s of series) {
      for (const datum of s.data ?? []) {
        const n = getValueFromDatum(datum);
        if (Number.isFinite(n) && n > maxValue) maxValue = n;
      }
    }
  }
  return Number.isFinite(maxValue) ? maxValue : undefined;
};

type ValueAxis = {
  type?: string;
  min?: number;
  max?: number;
  interval?: number;
  axisLabel?: {
    formatter?: string | ((value: number) => string);
    [key: string]: unknown;
  };
  splitLine?: {
    show?: boolean;
    lineStyle?: { color?: string | string[]; [key: string]: unknown };
    [key: string]: unknown;
  };
};

const EPSILON = 1e-9;

// Pick a "nice" axis interval — multiples of 1, 2, or 5 at the appropriate
// magnitude — aiming for ~5 segments. Mirrors echarts' built-in axis tick
// algorithm so soft-bounds labels match strict-bounds labels visually.
const pickNiceInterval = (max: number): number => {
  if (!Number.isFinite(max) || max <= 0) return 1;
  const rough = max / 5;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  let nice: number;
  if (normalized < 1.5) nice = 1;
  else if (normalized < 3) nice = 2;
  else if (normalized < 7) nice = 5;
  else nice = 10;
  return nice * magnitude;
};

const applyValueAxisBounds = (
  option: Record<string, unknown>,
  payload: Record<string, unknown>,
) => {
  if (payload.showAxisLabels === false) return;
  const userMin = parseBound(payload.valueAxisMin);
  const userMax = parseBound(payload.valueAxisMax);
  if (userMin === undefined && userMax === undefined) return;

  const allowHide = payload.allowBoundsHideData !== false;
  const xAxis = option.xAxis as ValueAxis | undefined;
  const yAxis = option.yAxis as ValueAxis | undefined;

  if (allowHide) {
    if (xAxis && xAxis.type === "value") {
      if (userMin !== undefined) xAxis.min = userMin;
      if (userMax !== undefined) xAxis.max = userMax;
    }
    if (yAxis && yAxis.type === "value") {
      if (userMin !== undefined) yAxis.min = userMin;
      if (userMax !== undefined) yAxis.max = userMax;
    }
    return;
  }

  const series = (option.series ?? []) as Array<{
    data?: unknown[];
    stack?: string;
  }>;

  // Soft mode: axis.min stays at userMin (clean visual bottom), axis.max
  // extends past userMax to fit data — labels/gridlines beyond userMax are
  // hidden via formatter + color array so the axis appears to "stop" at userMax
  // while bars still render fully.
  if (userMax === undefined || userMax <= 0) {
    if (userMin !== undefined) {
      if (xAxis && xAxis.type === "value") xAxis.min = userMin;
      if (yAxis && yAxis.type === "value") yAxis.min = userMin;
    }
    return;
  }
  const positiveMax = userMax;
  const axisStart = userMin ?? 0;

  const dataMax = getDataMax(series);
  const finalMax =
    dataMax !== undefined && dataMax > positiveMax ? dataMax : positiveMax;

  const applyToAxis = (axis: ValueAxis | undefined) => {
    if (!axis || axis.type !== "value") return;
    const interval = pickNiceInterval(positiveMax);
    const snappedMax = Math.ceil(finalMax / interval) * interval;
    axis.max = snappedMax;
    if (userMin !== undefined) axis.min = userMin;
    axis.interval = interval;

    const axisLabel = axis.axisLabel ?? {};
    const original = axisLabel.formatter;
    axisLabel.formatter = (value: number) => {
      if (value > positiveMax + EPSILON) return "";
      if (typeof original === "function") return original(value);
      if (typeof original === "string")
        return original.replace("{value}", String(value));
      return String(value);
    };
    axis.axisLabel = axisLabel;

    const splitLine = axis.splitLine;
    if (splitLine && splitLine.show !== false) {
      const baseColor =
        (typeof splitLine.lineStyle?.color === "string"
          ? splitLine.lineStyle.color
          : undefined) ?? "#E0E6F1";
      const lineCount = Math.ceil((snappedMax - axisStart) / interval) + 2;
      const colors: string[] = [];
      for (let i = 0; i <= lineCount; i++) {
        const value = axisStart + i * interval;
        colors.push(value > positiveMax + EPSILON ? "transparent" : baseColor);
      }
      axis.splitLine = {
        ...splitLine,
        lineStyle: {
          ...(splitLine.lineStyle ?? {}),
          color: colors,
        },
      };
    }
  };

  applyToAxis(xAxis);
  applyToAxis(yAxis);
};
